// A bedside-monitor sweep on a transparent canvas: traces are drawn by a pen moving left to right at a fixed
// paper speed, an erase bar runs just ahead of the pen and the oldest trace fades like phosphor before it goes.
// The grid (when there is one) sits behind the canvas, so erasing never touches it. One animation frame loop,
// paused off screen and in background tabs; with reduced motion the window is drawn once and held still.
// The layout is in the box's own CSS pixels (the stage's 1920 × 1080 frame shows the same seconds of trace at any
// window size) and the canvas holds as many pixels as the screen really shows, ancestors' transforms included, so the
// trace is crisp on a 3× phone and on a stage scaled up to a big window, and stays crisp when the window is resized.
import { canvasRatio, context2d, effectiveRatio, prefersReducedMotion, watchEffectiveRatio, watchVisibility } from '../env';

export interface Lane {
  /** Canvas y (CSS px) for stream time t (seconds). */
  y: (t: number) => number;
  colour: string;
  /** Line width in CSS px. */
  width: number;
}

export interface SweepLayout { width: number; height: number; pxPerSec: number }

export interface SweepOptions {
  canvas: HTMLCanvasElement;
  /** The element the canvas fills; its size drives the layout. */
  box: HTMLElement;
  /** Seconds of trace across the full width, for a given width. */
  secondsAcross: (width: number) => number;
  /** The lanes for a layout (rebuilt on every resize). */
  lanes: (layout: SweepLayout) => Lane[];
  /** Called after each layout (resize), before drawing. */
  onLayout?: (layout: SweepLayout) => void;
  /** Called every frame with the stream-time window just drawn (for beat-synchronised details). */
  onFrame?: (fromSec: number, toSec: number) => void;
}

/** Stream time starts this far in, so a full window of history can always be drawn behind the pen. */
const LEAD_SEC = 8;
const SUB = 0.5;   // px between samples
const GAP = 14;    // erase bar, px
const FADE = 28;   // phosphor fade zone ahead of the erase bar, px

export function startSweep(o: SweepOptions): () => void {
  const ctx = context2d(o.canvas);
  if (!ctx) return () => undefined;
  const c = ctx;
  const reduced = prefersReducedMotion();
  const t0 = performance.now();
  const streamTime = (now: number) => (now - t0) / 1000 + LEAD_SEC;

  let W = 0, H = 0, speed = 1;
  let lanes: Lane[] = [];
  const pen = { x: 0, t: LEAD_SEC, prev: null as number[] | null };

  const clearWrapped = (a: number, b: number) => {
    if (b <= a) return;
    if (b - a >= W) { c.clearRect(0, 0, W, H); return; }
    const s = ((a % W) + W) % W, e = s + (b - a);
    c.clearRect(s, 0, Math.min(e, W) - s, H);
    if (e > W) c.clearRect(0, 0, e - W, H);
  };
  const fadeWrapped = (a: number, b: number, alpha: number) => {
    if (b <= a || alpha <= 0) return;
    c.save();
    c.globalCompositeOperation = 'destination-out';
    c.fillStyle = `rgba(0,0,0,${Math.min(1, alpha)})`;
    const s = ((a % W) + W) % W, e = s + (b - a);
    c.fillRect(s, 0, Math.min(e, W) - s, H);
    if (e > W) c.fillRect(0, 0, e - W, H);
    c.restore();
  };

  const strokeLane = (lane: Lane, pts: number[][]) => {
    // pts: runs of [x, y, x, y, ...] split where the pen wrapped
    for (const run of pts) {
      if (run.length < 4) continue;
      c.beginPath();
      c.moveTo(run[0], run[1]);
      for (let i = 2; i < run.length; i += 2) c.lineTo(run[i], run[i + 1]);
      c.strokeStyle = lane.colour;
      c.lineJoin = 'round';
      c.globalAlpha = 0.18;
      c.lineWidth = lane.width + 3.2;
      c.lineCap = 'butt';
      c.stroke();
      c.globalAlpha = 1;
      c.lineWidth = lane.width;
      c.lineCap = 'round';
      c.stroke();
    }
  };

  /** Move the pen to stream time `to`, drawing every lane. */
  const step = (to: number, maxDt = 0.25) => {
    let dt = to - pen.t;
    if (dt <= 0 || W <= 0) return;
    if (dt > maxDt) { pen.t = to - maxDt; dt = maxDt; }
    const x0 = pen.x, x1 = x0 + dt * speed;
    clearWrapped(x0 + 1, x1 + GAP);
    fadeWrapped(x1 + GAP, x1 + GAP + FADE, 1 - Math.pow(0.94, x1 - x0));
    const nextPrev: number[] = [];
    lanes.forEach((lane, li) => {
      const runs: number[][] = [];
      let run: number[] = [];
      const prevY = pen.prev?.[li];
      if (prevY !== undefined) run.push(x0 % W, prevY);
      let lastX = x0 % W;
      let y = prevY ?? lane.y(pen.t);
      for (let x = Math.floor(x0 / SUB) * SUB + SUB; x <= x1 + 1e-9; x += SUB) {
        const t = pen.t + (x - x0) / speed;
        y = lane.y(t);
        const X = x % W;
        if (X < lastX) { runs.push(run); run = [X, y]; } else run.push(X, y);
        lastX = X;
      }
      runs.push(run);
      strokeLane(lane, runs);
      nextPrev[li] = y;
    });
    pen.prev = nextPrev;
    pen.x = x1 % W;
    pen.t = to;
  };

  /** A full window of history behind the pen, so the trace is complete from the first frame. */
  const prefill = (tEnd: number) => {
    c.clearRect(0, 0, W, H);
    pen.x = 0;
    pen.prev = null;
    pen.t = tEnd - W / speed;
    step(tEnd, Infinity);
  };

  /** The same window again, with the pen where it was: after a change of pixel density only, so the sweep does not jump. */
  const redraw = () => {
    const x = pen.x, t = pen.t;
    pen.prev = null;
    pen.t = t - W / speed;
    pen.x = x;
    step(t, Infinity);
  };

  /** Reduced motion: the window ending at tEnd (fixed when first drawn), drawn once, left to right, no erase bar. */
  let stillEnd = 0;
  const drawStill = () => {
    c.clearRect(0, 0, W, H);
    const from = stillEnd - W / speed;
    for (const lane of lanes) {
      const run: number[] = [];
      for (let x = 0; x <= W; x += SUB) run.push(x, lane.y(from + x / speed));
      strokeLane(lane, [run]);
    }
  };

  /**
   * Fits the canvas to the box: its layout size in CSS pixels (before any ancestor's transform), at the effective ratio.
   * 'size' when the layout size changed (lanes rebuilt), 'ratio' when only the pixel density did, null when neither did.
   */
  let R = 0;
  const layout = (): 'size' | 'ratio' | null => {
    const w = Math.max(1, o.box.offsetWidth), h = Math.max(1, o.box.offsetHeight);
    const r = canvasRatio(effectiveRatio(o.box), w, h);
    const sized = w !== W || h !== H;
    if (!sized && Math.round(w * r) === o.canvas.width && Math.round(h * r) === o.canvas.height) return null;
    W = w; H = h; R = r;
    o.canvas.width = Math.max(1, Math.round(W * R));
    o.canvas.height = Math.max(1, Math.round(H * R));
    o.canvas.style.width = `${W}px`;
    o.canvas.style.height = `${H}px`;
    c.setTransform(R, 0, 0, R, 0, 0);
    if (!sized) return 'ratio';
    speed = W / Math.max(0.5, o.secondsAcross(W));
    const l: SweepLayout = { width: W, height: H, pxPerSec: speed };
    lanes = o.lanes(l);
    o.onLayout?.(l);
    return 'size';
  };

  let raf = 0;
  let running = false;
  const frame = (now: number) => {
    if (!running) return;
    const from = pen.t;
    const t = streamTime(now);
    step(t);
    o.onFrame?.(from, t);
    raf = requestAnimationFrame(frame);
  };
  const start = () => {
    if (running || reduced) return;
    running = true;
    prefill(streamTime(performance.now()));
    raf = requestAnimationFrame(frame);
  };
  const stop = () => { running = false; cancelAnimationFrame(raf); };

  layout();
  if (reduced) { stillEnd = streamTime(performance.now()); drawStill(); }

  // A new layout size starts the sweep over; a new pixel density (the window resized around the stage, the window moved to
  // another screen, an ancestor's transform settled) redraws what was there at the new resolution.
  const onResize = () => {
    const what = layout();
    if (!what) return;
    if (reduced) drawStill();
    else if (what === 'size') prefill(streamTime(performance.now()));
    else redraw();
  };
  let ro: ResizeObserver | undefined;
  if (typeof ResizeObserver === 'function') { ro = new ResizeObserver(onResize); ro.observe(o.box); }
  else window.addEventListener('resize', onResize);
  const unwatchRatio = watchEffectiveRatio(o.box, onResize);
  const unwatch = watchVisibility(o.box, (visible) => (visible ? start() : stop()));

  return () => {
    stop();
    unwatch();
    unwatchRatio();
    if (ro) ro.disconnect(); else window.removeEventListener('resize', onResize);
  };
}
